import React, { useEffect, useState } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import CompanyNavigation from './CompanyNavigation';
import CompanyFooter from './CompanyFooter';
import Icon from './icons';
import PDFExportButton from '../PDFExportButton';
import { getService } from '../../data/services';
import '../../styles/Company.css';

const ServicePage: React.FC = () => {
  const { slug } = useParams();
  const service = getService(slug);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    if (service) {
      document.title = `${service.name} — Waymining`;
    }
    window.scrollTo(0, 0);
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [service]);

  if (!service) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="company-page" id="service-page-container">
      <CompanyNavigation scrolled={scrolled} />

      <section className="service-hero">
        <div className="company-container">
          <div className="service-hero-icon">
            <Icon name={service.icon} className="w-10 h-10" />
          </div>
          <span className="company-card-tag">Service</span>
          <h1>{service.name}</h1>
          <p className="service-tagline">{service.tagline}</p>

          <div className="service-hero-stats">
            <div className="service-hero-stat">
              <span className="service-hero-stat-icon">🛠️</span>
              <span>Design &amp; Integration</span>
            </div>
            <div className="service-hero-stat">
              <span className="service-hero-stat-icon">📞</span>
              <span>Ongoing Support</span>
            </div>
            <div className="service-hero-stat">
              <span className="service-hero-stat-icon">📊</span>
              <span>Reporting &amp; Analytics</span>
            </div>
          </div>
        </div>
      </section>

      <section className="service-body">
        <div className="company-container service-grid">
          <div className="service-description">
            <h2>Overview</h2>
            <p>{service.description}</p>
          </div>

          <div className="service-features">
            <h2>What we deliver</h2>
            <ul>
              {service.features.map((f, i) => (
                <li key={i}>
                  <Check size={18} className="service-check" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="service-benefits-section">
        <div className="company-container">
          <div className="company-section-header">
            <h2>Why choose this service</h2>
          </div>
          <div className="service-benefits-grid">
            {service.benefits.map((b, i) => (
              <div key={i} className="service-benefit-card">
                <h3>{b.title}</h3>
                <p>{b.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="service-cta">
        <div className="company-container service-cta-inner">
          <div>
            <h2>Ready to talk about {service.name}?</h2>
            <p>Get in touch and we'll walk you through how it fits your operation.</p>
          </div>
          <div className="service-actions">
            <a href="#contact" className="company-btn-primary">
              Request a consultation <ArrowRight size={18} />
            </a>
            <Link to="/#services" className="company-btn-secondary">
              Back to all services
            </Link>
          </div>
        </div>
      </section>

      <CompanyFooter />
      <PDFExportButton
        variant="floating"
        filename={`waymining-${service.slug}`}
        elementId="service-page-container"
      />
    </div>
  );
};

export default ServicePage;
